'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Suspense, useCallback, useEffect, useState } from 'react'
import {
  AuthDivider,
  AuthError,
  AuthField,
  AuthGoogleButton,
  AuthHeading,
  AuthPasswordField,
  AuthPrimaryButton,
} from '@/components/auth/auth-form-primitives'
import { AuthShell } from '@/components/auth/auth-shell'
import { createClient } from '@/lib/supabase/client'

function SignupForm() {
  const t = useTranslations('Auth')
  const router = useRouter()
  const supabase = createClient()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const checkAuth = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!cancelled && user) {
          router.push('/onboarding')
          return
        }
        if (!cancelled) {
          setIsCheckingAuth(false)
        }
      } catch (authError) {
        console.error('Signup auth check failed:', authError)
        if (!cancelled) {
          setIsCheckingAuth(false)
        }
      }
    }

    checkAuth()
    return () => {
      cancelled = true
    }
  }, [router, supabase.auth])

  const handleSignup = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setIsLoading(true)
      setError(null)

      try {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
            },
          },
        })

        if (signUpError) {
          console.error('[signup] Account creation rejected', signUpError)
          setError(t('signupFailed'))
          setIsLoading(false)
          return
        }

        router.refresh()
        router.push('/onboarding')
        setIsLoading(false)
      } catch (err) {
        console.error('Signup failed:', err)
        setError(t('signupFailed'))
        setIsLoading(false)
      }
    },
    [email, fullName, password, router, supabase.auth, t]
  )

  const handleGoogleSignIn = useCallback(async () => {
    setIsLoading(true)
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent('/onboarding')}`,
      },
    })
  }, [supabase.auth])

  const handleFullNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setFullName(event.target.value),
    []
  )
  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value),
    []
  )
  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value),
    []
  )

  if (isCheckingAuth) {
    return (
      <AuthShell>
        <div
          aria-hidden="true"
          style={{ display: 'flex', flexDirection: 'column', gap: '24px', opacity: 0.35 }}
        >
          <div
            style={{ height: '48px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
          />
          <div
            style={{ height: '72px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
          />
          <div
            style={{ height: '52px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
          />
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <form
        onSubmit={handleSignup}
        style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}
      >
        <AuthHeading subtitle={t('signUpToGetStarted')} title={t('createAccount')} />

        <AuthField
          autoComplete="name"
          id="full-name"
          label={t('fullNameLabel')}
          onChange={handleFullNameChange}
          placeholder={t('fullNamePlaceholder')}
          required
          value={fullName}
        />

        <AuthField
          autoComplete="email"
          id="email"
          label={t('emailLabel')}
          onChange={handleEmailChange}
          placeholder={t('emailPlaceholder')}
          required
          type="email"
          value={email}
        />

        <AuthPasswordField
          autoComplete="new-password"
          id="password"
          label={t('passwordLabel')}
          minLength={6}
          name="password"
          onChange={handlePasswordChange}
          placeholder={t('passwordPlaceholder')}
          required
          value={password}
        />

        {error ? <AuthError>{error}</AuthError> : null}

        <AuthPrimaryButton disabled={isLoading} isLoading={isLoading} type="submit">
          {isLoading ? t('signingUp') : t('signUp')}
        </AuthPrimaryButton>

        <AuthDivider label={t('orContinueWith')} />

        <AuthGoogleButton
          disabled={isLoading}
          label={t('google')}
          onClick={handleGoogleSignIn}
          type="button"
        />

        <p
          style={{
            fontFamily: 'var(--font-geist), ui-sans-serif, sans-serif',
            fontSize: '14px',
            color: 'hsl(var(--muted-foreground))',
            textAlign: 'center',
            margin: 0,
          }}
        >
          {t('alreadyHaveAccount')}{' '}
          <Link
            href="/login"
            style={{
              color: 'hsl(var(--foreground))',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {t('signIn')}
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}

export default function SignupPage() {
  return (
    <Suspense
      fallback={
        <AuthShell>
          <div
            aria-hidden="true"
            style={{ opacity: 0.35, display: 'flex', flexDirection: 'column', gap: '24px' }}
          >
            <div
              style={{ height: '48px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
            />
            <div
              style={{ height: '72px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
            />
            <div
              style={{ height: '52px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
            />
          </div>
        </AuthShell>
      }
    >
      <SignupForm />
    </Suspense>
  )
}
